import React, { createContext, useContext, useState } from 'react';
import { capabilitiesForRole, type Capability } from '../config/navigation';
import { ACTIONS_MARKER, type ActionKey } from '../config/actions';
import { FACILITY_SCOPE, isFullAccessRole } from '../config/roles';
import { resetPaginationConfig } from '../hooks/usePaginationConfig';
import { resetCompanyProfile } from '../api/companyProfile';

interface User {
  email: string;
  name?: string;
  role?: string;
  companyRoleId?: string | null;
  roleName?: string;
  rolePermissions?: string[];
  assignedWarehouseIds?: string[];
  companyId?: string;
  companyLogo?: string | null;
  companyName?: string | null;
  activeFeatures?: string[];
  activePlanName?: string | null;
  noPlanNeed?: boolean;
  /**
   * Set by the server on a company admin's FIRST sign-in, when their company
   * has no ship-from address yet. The server stamps the first login as it
   * answers, so this is true exactly once per account and never again.
   *
   * Cleared locally once the wizard closes, so a page refresh inside the same
   * session does not reopen it.
   */
  showSetupWizard?: boolean;
  access_token?: string;
}

interface AuthContextType {
  user: User | null;
  login: (userData: User) => void;
  logout: () => void;
  updateUser: (data: Partial<User>) => void;
  hasFeature: (featureCode: string) => boolean;
  hasMenuPermission: (menuId: string) => boolean;
  /** May the user do this on a page they can open? Hide the control when false. */
  hasAction: (action: ActionKey) => boolean;
  /**
   * Is the user confined to their assigned facilities? By their role's data
   * scope, or by having been assigned some; never for company admins.
   */
  isFacilityRestricted: () => boolean;
  can: (capability: Capability) => boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(() => {
    const storedUser = localStorage.getItem('user');
    try {
      return storedUser ? JSON.parse(storedUser) : null;
    } catch (error) {
      console.error('Failed to parse stored user:', error);
      return null;
    }
  });

  // useEffect removed as state is now initialized synchronously

  // Helper to dynamically get domain, path, and cookie name per environment
  const getCookieDomain = () => {
    if (import.meta.env.VITE_COOKIE_DOMAIN) {
      return import.meta.env.VITE_COOKIE_DOMAIN;
    }
    const parts = window.location.hostname.split('.');
    if (parts.length >= 2) {
      return '.' + parts.slice(-2).join('.');
    }
    return window.location.hostname;
  };

  const getCookiePath = () => {
    return import.meta.env.VITE_COOKIE_PATH || '/';
  };

  const getCookieName = () => {
    return import.meta.env.VITE_COOKIE_NAME || 'is_logged_in';
  };

  const login = (userData: User) => {
    localStorage.setItem('user', JSON.stringify(userData));
    const domain = getCookieDomain();
    const path = getCookiePath();
    const cookieName = getCookieName();

    // Set environment specific cookie to prevent collisions between local, staging, and prod
    document.cookie = `${cookieName}=true; domain=${domain}; path=${path}; max-age=86400`;
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('user');
    // The grid page size is a company setting cached in localStorage; the next
    // person to sign in on this browser may belong to a different company.
    resetPaginationConfig();
    resetCompanyProfile();
    const domain = getCookieDomain();
    const path = getCookiePath();
    const cookieName = getCookieName();

    document.cookie = `${cookieName}=; domain=${domain}; path=${path}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    document.cookie = `is_logged_in=; domain=${domain}; path=${path}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    document.cookie = `is_logged_in_prod=; domain=${domain}; path=${path}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    document.cookie = `is_logged_in_staging=; domain=${domain}; path=${path}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    document.cookie = `is_logged_in_dev=; domain=${domain}; path=${path}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    setUser(null);
  };

  const updateUser = (data: Partial<User>) => {
    setUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, ...data };
      localStorage.setItem('user', JSON.stringify(updated));
      return updated;
    });
  };

  const hasFeature = (featureCode: string): boolean => {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    
    // Fail closed: no plan loaded means no plan features. Routes wait for the
    // plan instead of reading this as a refusal (see NavGuard), and the API
    // enforces the same rule regardless.
    if (!user.activeFeatures || !Array.isArray(user.activeFeatures)) {
      return false;
    }

    const features = user.activeFeatures;
    if (features.includes('*') || features.includes(featureCode)) return true;

    // Alias fallback mappings
    const aliasMap: Record<string, string[]> = {
      get_quotation: ['rates'],
      rates: ['get_quotation'],
      // Carrier connections are unlimited on every plan, so `carrier` is satisfied
      // by any of the three billable features rather than by a plan row of its own.
      carrier: ['carrier_integrations', 'shipment', 'rates', 'tracing'],
      carrier_integrations: ['carrier', 'shipment', 'rates', 'tracing'],
      shipping_tracking_history: ['shipment_tracking'],
      shipment_tracking: ['shipping_tracking_history'],
      internal_tickets: ['tracing', 'ticket_intervention'],
      ticket_intervention: ['tracing', 'internal_tickets'],
      tracing: ['ticket_intervention', 'internal_tickets'],
      // Auto batch is an automation layer over shipping, granted by `shipment`.
      auto_batch: ['auto_batch_logs', 'shipment'],
      auto_batch_logs: ['auto_batch', 'shipment'],
      email_logs: ['smtp_logs'],
      smtp_logs: ['email_logs'],
    };

    const aliases = aliasMap[featureCode] || [];
    return aliases.some((alias) => features.includes(alias));
  };

  const hasMenuPermission = (menuId: string): boolean => {
    if (!user) return false;
    if (isFullAccessRole(user.role)) return true;
    // Self-service pages, not role menus: every signed-in user has them.
    if (menuId === 'profile' || menuId === 'password' || menuId === 'support') return true;
    // Fail closed: a non-admin without a menu list has no menus. Login and the
    // profile refresh both deliver one, so this only bites a broken session.
    if (!user.rolePermissions || !Array.isArray(user.rolePermissions)) return false;
    if (user.rolePermissions.includes('*') || user.rolePermissions.includes(menuId)) return true;
    return false;
  };

  const hasAction = (action: ActionKey): boolean => {
    if (!user) return false;
    if (isFullAccessRole(user.role)) return true;
    const list = user.rolePermissions;
    if (!Array.isArray(list)) return false;
    if (list.includes('*') || list.includes(action)) return true;
    // A list from before actions existed: the API decides (see config/actions.ts).
    return !list.includes(ACTIONS_MARKER);
  };

  const isFacilityRestricted = (): boolean => {
    if (!user || isFullAccessRole(user.role)) return false;
    if (Array.isArray(user.rolePermissions) && user.rolePermissions.includes(FACILITY_SCOPE)) return true;
    return Array.isArray(user.assignedWarehouseIds) && user.assignedWarehouseIds.length > 0;
  };

  /**
   * Capability check. Prefer this over comparing `user.role` in components —
   * the role → capability map lives in config/navigation.ts.
   */
  const can = (capability: Capability): boolean => {
    if (!user) return false;
    if (capability === 'account.self') return true;
    if (!user.role) return false;
    return capabilitiesForRole(user.role).includes(capability);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, updateUser, hasFeature, hasMenuPermission, hasAction, isFacilityRestricted, can, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
